import api from './api';

export interface ExportJob {
  id: string;
  exportType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  progress: number;
  totalRows: number | null;
  processedRows: number;
  fileName: string | null;
  fileSize: number | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface CreateExportParams {
  filters: Record<string, any>;
  columns?: { key: string; header: string }[];
}

/**
 * Entity-specific export configuration
 */
export interface ExportEntityConfig {
  /**
   * The export type (e.g., 'ALERTS', 'CASES', 'INCIDENTS')
   */
  exportType: string;
  
  /**
   * The API endpoint base path (e.g., '/alerts', '/cases')
   */
  endpoint: string;
}

/**
 * Create a new export job
 */
export async function createExportJob(
  config: ExportEntityConfig,
  params: CreateExportParams
): Promise<string> {
  const response = await api.post(`${config.endpoint}/export`, params);
  return response.data.jobId;
}

/**
 * Get export job status
 */
export async function getExportJobStatus(
  config: ExportEntityConfig,
  jobId: string
): Promise<ExportJob | null> {
  try {
    const response = await api.get(`${config.endpoint}/export/${jobId}/status`);
    return response.data.job;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Download export file
 * For Azure storage, uses direct SAS URL. For local storage, downloads via API.
 */
export async function downloadExportFile(
  config: ExportEntityConfig,
  jobId: string,
  fileName: string,
  downloadUrl?: string | null
): Promise<void> {
  // If we have a direct download URL (Azure SAS URL), use it directly
  if (downloadUrl && downloadUrl.startsWith('https://')) {
    // Open the SAS URL in a new window/tab to trigger download
    // This avoids CORS issues with Azure Blob Storage
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  // For local storage or relative URLs, download via API
  const response = await api.get(`${config.endpoint}/export/${jobId}/download`, {
    responseType: 'blob',
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Delete export job and its associated file (cleanup)
 */
export async function deleteExportJob(
  config: ExportEntityConfig,
  jobId: string
): Promise<void> {
  await api.delete(`${config.endpoint}/export/${jobId}`);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Predefined export configurations for common entities
 */
export const ExportConfigs = {
  ALERTS: {
    exportType: 'ALERTS',
    endpoint: '/alerts',
  } as ExportEntityConfig,
  CASES: {
    exportType: 'CASES',
    endpoint: '/cases',
  } as ExportEntityConfig,
  INCIDENTS: {
    exportType: 'INCIDENTS',
    endpoint: '/incidents',
  } as ExportEntityConfig,
  PROBLEMS: {
    exportType: 'PROBLEMS',
    endpoint: '/problems',
  } as ExportEntityConfig,
  CHANGES: {
    exportType: 'CHANGES',
    endpoint: '/changes',
  } as ExportEntityConfig,
  SERVICE_REQUESTS: {
    exportType: 'SERVICE_REQUESTS',
    endpoint: '/service-requests',
  } as ExportEntityConfig,
};

